import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  /** `contact.formUnavailable` from the dictionary; the email link follows it. */
  message: string
  /** The sede's public email. */
  email: string
  children: ReactNode
}

interface State {
  failed: boolean
}

/**
 * Catches a failed load (or crash) of the lazy contact-form chunk — a
 * deploy that removed the old hashed file, a flaky network — so the band
 * shows a mailto line instead of React unmounting the whole page. Only
 * ever renders children on the server and in the first client render, so
 * hydration is unaffected.
 */
export class FormErrorBoundary extends Component<Props, State> {
  state: State = { failed: false }

  static getDerivedStateFromError(): State {
    return { failed: true }
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error('[verde-vercelli-2] contact form failed to load', error, info.componentStack)
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children
    const { message, email } = this.props
    return (
      <p className="rounded-2xl border border-white/15 bg-white/10 p-6 text-center text-vc2-on-forest">
        {message}{' '}
        <a href={`mailto:${email}`} className="underline underline-offset-4 [overflow-wrap:anywhere]">
          {email}
        </a>
      </p>
    )
  }
}
